<script setup>
import { useUserStore } from "@store/user";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import NotificationApi from "@api/notifications.api";
import { useLoaderStore } from "@store/loader";
import { useEventBus } from '@vueuse/core';
import UnCollapse from "../../../assets/svg/unCollapse.svg?component";
import Collapse from "../../../assets/svg/collapse.svg?component";
import DocumentForm from "@views/documents/Create.vue";
import ProjectForm from "@views/projects/Create.vue";
import cancelModal from "../../../components/cancelModal.vue";
import AfterCreateProjectModal from "../../projects/AfterCreateProjectModal.vue";
import AfterCreateDocumentModal from "../../documents/AfterCreateDocumentModal.vue";
import History from "./History.vue";
import SaveState from "@services/navigation/SaveState.js";

import NewSearchComponent from "../../../components/NewSearchModal.vue";
import RecentSearchesSidebar from "../../../components/RecentSearchesSidebar.vue";
import FullSearchResultsSidebar from "../../../components/FullSearchResultsSidebar.vue";

import ListForm from "@views/lists/Create/Create.vue";
import AfterCreateListModal from "../../lists/Create/AfterCreateListModal.vue";

const props = defineProps({
    collapse: { type: Boolean, default: false },
    handleOpen: { type: Function, default: null },
    handleClose: { type: Function, default: null },
});
const emit = defineEmits(["toggle"]);

// History navigation state
const showHistoryDropdown = ref(false);
const showUserDropdown = ref(false);
const showSearchesDropdown = ref(false);

// Search modal state
const showSearchModal = ref(false);
const showRecentSearchesModal = ref(false);
const showFullSearchResultsModal = ref(false);
const fullSearchQuery = ref("");
const fullSearchInitialTab = ref("project");

const loaderStore = useLoaderStore();
const store = useUserStore();
const bus = useEventBus();

// Listen for search modal events from other components
bus.on('open-search-modal', () => {
    openSearchModal()
});

let userId = computed(() => {
    return useUserStore().user.id;
});
const route = useRoute();
const router = useRouter();
const notifyBadge = ref(false);
// Upload / Create modals state
const createDocument = ref(false);
const createProject = ref(false);
const editProject = ref(null);
const showAfterCreateProjectModal = ref(false);
const showAfterCreateDocumentModal = ref(false);
const createdProjectId = ref(null);
const createdDocumentId = ref(null);

const createList = ref(false);
const editList = ref(null);
const createdListId = ref(null);
const item_ids = ref([]);
const items_type = ref([]);
const showAfterCreateListModal = ref(false);

const modalTitle = ref("Upload New Document");
const defaultCategory = ref(null);
const defaultAutoProceed = ref(false);
const defaultProjectId = ref(null);
const logout = async () => {
    useUserStore()
        .logout()
        .then(() => {
            localStorage.clear();
            router.push({ name: "login" });
        })
        .catch((error) => {
            console.log(error);
        });
};

let notifications = ref([]);
let unreadCount = ref(0);
let notificationsLength = ref(0);
let filters = ref({
    limit: 3,
    page: 1,
    order: { created_at: "desc" },
    AllNotifications: userId,
});
let paginationData = ref({});

const getNotifications = () => {
    NotificationApi.getUserAllNotification(filters.value).then((response) => {
        notifications.value = response.data.notifications;
        notificationsLength.value = response.data.length;
        unreadCount.value = 0;
        notifications.value.forEach((notification) => {
            if (notification.is_read === false) {
                unreadCount.value += 1;
            }
        });
    });
};

const markAsRead = (notification = "") => {
    if (notification == "") {
        unreadCount.value = 0;
    }
    NotificationApi.markAsRead({
        id: notification ? notification.id : "",
    }).then((response) => {
        getNotifications();
        if (notification != "") {
            router.push({
                name:
                    notification.type === "project"
                        ? "projects.view"
                        : "documents.view",
                params: { id: notification.resource_id },
            });
        }
        getNotifications();
    });
};

const markAsSeen = (notification = "") => {
    if (notification == "") {
        unreadCount.value = 0;
    }
    NotificationApi.markAsSeen({
        id: notification ? notification.id : "",
    }).then((response) => {
        getNotifications();
        if (notification != "") {
            router.push({
                name:
                    notification.type === "project"
                        ? "projects.view"
                        : "documents.view",
                params: { id: notification.resource_id },
            });
        }
        getNotifications();
    });
};

const afterCreateProject = (project) => {
    createProject.value = false;
    createdProjectId.value = project.id;
    showAfterCreateProjectModal.value = true;
};

const afterCreateDocument = (Document) => {
    createDocument.value = false;
    createdDocumentId.value = Document.id;
    showAfterCreateDocumentModal.value = true;
};

const afterCreateList = (list) => {
    createList.value = false;
    editList.value = null;
    createdListId.value = list?.id ?? null;
    if (list?.id) {
        showAfterCreateListModal.value = true;
    }
};

const handleListUpdated = () => {
    createList.value = false;
    editList.value = null;
};

onMounted(() => {
    unreadCount.value = 0;
    getNotifications();

    Echo.channel("notification-channel").listen(
        ".notification-event",
        (event) => {
            notifyBadge.value = true;
            getNotifications();
        },
    );

    // Listen for global open events
    window.addEventListener("open-upload-document", (e) => {
        defaultCategory.value = e?.detail?.category ?? null;
        defaultAutoProceed.value = !!e?.detail?.autoProceedToStep2;
        defaultProjectId.value = e?.detail?.projectId ?? null;
        modalTitle.value = "Upload New Document";
        createDocument.value = true;
    });
    window.addEventListener("open-create-project", (e) => {
        editProject.value = e?.detail?.project || null;
        createProject.value = true;
    });
    window.addEventListener("open-create-list", (e) => {
        console.log("Received create-list event detail:", e?.detail);

        editList.value = e?.detail?.list || null;
        item_ids.value = e?.detail?.item_ids || [];
        items_type.value = e?.detail?.items_type || []; // projects , documents , tamers
        createList.value = true;
    });
});

watch(fetchNotification, (currentValue) => {
    if (currentValue) {
        getNotifications();
    }
});

const openNotifyPopup = () => {
    notifyBadge.value = false;
    markAsRead("");
};

// History navigation methods
const toggleHistoryDropdown = () => {
    showHistoryDropdown.value = !showHistoryDropdown.value;
    console.log("toggleHistoryDropdown", showHistoryDropdown.value);
    showUserDropdown.value = false;
};

const toggleUserDropdown = () => {
    showUserDropdown.value = !showUserDropdown.value;
    showHistoryDropdown.value = false;
    showSearchesDropdown.value = false;
};

const toggleSearchesDropdown = () => {
    showSearchesDropdown.value = !showSearchesDropdown.value;
    showHistoryDropdown.value = false;
    showUserDropdown.value = false;
};

// Search modal functions
const openSearchModal = () => {
    showSearchModal.value = true;
    showHistoryDropdown.value = false;
    showUserDropdown.value = false;
};

const closeSearchModal = () => {
    showSearchModal.value = false;
};

// Recent searches modal functions
const openRecentSearchesModal = () => {
    showRecentSearchesModal.value = true;
    showSearchesDropdown.value = false;
};

const closeRecentSearchesModal = () => {
    showRecentSearchesModal.value = false;
};

// Full search results modal functions
const openFullSearchResultsModal = (query, tab = "project") => {
    fullSearchQuery.value = query;
    fullSearchInitialTab.value = tab;
    showFullSearchResultsModal.value = true;
};

const closeFullSearchResultsModal = () => {
    showFullSearchResultsModal.value = false;
};

const handleSearchSelected = (query) => {
    // Open the main search modal with the selected query
    showSearchModal.value = true;
    showRecentSearchesModal.value = false;

    // Emit event to NewSearchComponent with the query
    window.dispatchEvent(
        new CustomEvent("search-query-selected", {
            detail: { query },
        }),
    );
};

const goToSavedSearches = () => {
    showSearchesDropdown.value = false;
    // Navigate to saved searches page
    router.push({ name: "saved-searches" });
};

// Dialog close confirmation
const showCancelDialog = ref(false);
const pendingCloseTarget = ref(null);
const openCloseDialog = () => {
    showCancelDialog.value = true;
};
const confirmCancel = () => {
    if (pendingCloseTarget.value === "document") {
        createDocument.value = false;
    } else if (pendingCloseTarget.value === "project") {
        createProject.value = false;
        editProject.value = null;
    } else if (pendingCloseTarget.value === "list") {
        createList.value = false;
        editList.value = null;
    }
    showCancelDialog.value = false;
    pendingCloseTarget.value = null;
};
const backToDialog = () => {
    showCancelDialog.value = false;
};

// Element Plus before-close hooks to keep dialog open until user confirms
const onBeforeCloseDocument = () => {
    pendingCloseTarget.value = "document";
    openCloseDialog();
};
const onBeforeCloseProject = () => {
    pendingCloseTarget.value = "project";
    openCloseDialog();
};
const onBeforeCloseList = () => {
    pendingCloseTarget.value = "list";
    openCloseDialog();
};

// const handleToggle = () => {
//     const newSideBar = document.querySelector('.newSideBar');
//     if (newSideBar) {
//         newSideBar.classList.remove('toggled');
//     }
//     console.log('TopBar toggle clicked, current isCollapsed:', props.isCollapsed);
//     // emit('toggle');
// }

const isCollapsed = ref(props.collapse);

watch(
    () => props.collapse,
    (newValue) => {
        isCollapsed.value = newValue;
    },
);

const handleToggle = () => {
    isCollapsed.value = !isCollapsed.value;
    emit("toggle", isCollapsed.value);
};

const saveState = async (type) => {
    const data = await SaveState.saveState(router.currentRoute.value, type);

    // Route forward or back after saveState returns data
    if (data && data.url) {
        console.log("data", data);
        if (type == "forward") {
            router.go(1);
        } else {
            router.back();
        }
    }
};
</script>

<template>
    <nav class="topbar-nav">
        <div class="topbar-container">
            <!-- Left Side - History Navigation -->
            <div class="nav-left">
                <div class="toggle-btn d-block d-lg-none" @click="handleToggle">
                    <UnCollapse class="nav-link-text" />
                </div>

                <div class="history-navigation">
                    <button
                        class="nav-btn"
                        :class="{ active: showHistoryDropdown }"
                        @click="toggleHistoryDropdown"
                        title="History"
                    >
                        <img
                            width="17"
                            height="17"
                            src="../../../assets/img/clock.png"
                            alt="Notification"
                        />
                    </button>
                    <button
                        :disabled="loaderStore.isLoading"
                        class="nav-btn navigate-arrow"
                        title="Back"
                        @click.prevent="saveState('back')"
                    >
                        <img
                            width="17"
                            height="17"
                            src="../../../assets/img/backword.png"
                            alt="Notification"
                        />
                    </button>
                    <button
                        :disabled="loaderStore.isLoading"
                        class="nav-btn navigate-arrow"
                        title="Forward"
                        @click.prevent="saveState('forward')"
                    >
                        <img
                            width="16"
                            height="16"
                            src="../../../assets/img/forward.png"
                            alt="Notification"
                        />
                    </button>
                    <span class="nav-text navigation-text">To Navigate</span>
                </div>

                <History
                    :showHistoryDropdown="showHistoryDropdown"
                    @close="showHistoryDropdown = false"
                    @open="showHistoryDropdown = true"
                />
            </div>

            <!-- Middle - Search -->
            <div class="nav-center">
                <div class="search-container">
                    <div class="search-icon">
                        <font-awesome-icon icon="fa-solid fa-search" class="nav-link-icon" />
                    </div>
                    <input
                        class="search-input"
                        placeholder="Ask Anything in Osharek Knowledge Base (Projects,Docs,TAMer(s)…)"
                        readonly
                        @click="openSearchModal"
                    />
                    <button class="search-ai-btn" @click="openSearchModal">
                        <svg
                            width="17"
                            height="16"
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                opacity="0.4"
                                d="M7.16569 4.1665C6.95647 4.1665 6.76937 4.29677 6.69676 4.49299L6.35287 5.42235C5.88605 6.68391 5.69634 7.16579 5.34732 7.5148C4.99831 7.86382 4.51643 8.05353 3.25487 8.52035L2.32551 8.86424C2.12929 8.93685 1.99902 9.12395 1.99902 9.33317C1.99902 9.54239 2.12929 9.72949 2.32551 9.8021L3.25488 10.146C4.51643 10.6128 4.99831 10.8025 5.34732 11.1515C5.69634 11.5006 5.88605 11.9824 6.35287 13.244L6.69676 14.1734C6.76937 14.3696 6.95647 14.4998 7.16569 14.4998C7.37491 14.4998 7.56201 14.3696 7.63462 14.1734L7.97851 13.244C8.44533 11.9824 8.63504 11.5006 8.98406 11.1515C9.33307 10.8025 9.81495 10.6128 11.0765 10.146L12.0059 9.80209C12.2021 9.72949 12.3324 9.54239 12.3324 9.33317C12.3324 9.12395 12.2021 8.93685 12.0059 8.86424L11.0765 8.52035C9.81495 8.05353 9.33307 7.86382 8.98406 7.5148C8.63504 7.16579 8.44533 6.68391 7.97851 5.42235L7.63462 4.49299C7.56201 4.29677 7.37491 4.1665 7.16569 4.1665Z"
                                fill="#412CC9"
                            />
                            <path
                                d="M12.499 6.5C12.7081 6.5 12.8951 6.36986 12.9678 6.17383L13.1152 5.77539C13.3244 5.21021 13.3855 5.07646 13.4805 4.98145C13.5755 4.88643 13.7092 4.82535 14.2744 4.61621L14.6729 4.46875C14.8689 4.39606 14.999 4.2091 14.999 4C14.999 3.7909 14.8689 3.60394 14.6729 3.53125L14.2744 3.38379C13.7092 3.17465 13.5755 3.11357 13.4805 3.01855C13.3855 2.92354 13.3244 2.78979 13.1152 2.22461L12.9678 1.82617C12.8951 1.63014 12.7081 1.5 12.499 1.5C12.2899 1.5 12.103 1.63014 12.0303 1.82617L11.8828 2.22461C11.6737 2.78979 11.6126 2.92354 11.5176 3.01855C11.4226 3.11357 11.2888 3.17465 10.7236 3.38379L10.3252 3.53125C10.1292 3.60394 9.99902 3.7909 9.99902 4C9.99902 4.2091 10.1292 4.39605 10.3252 4.46875L10.7236 4.61621C11.2888 4.82535 11.4226 4.88643 11.5176 4.98145C11.6126 5.07646 11.6737 5.21021 11.8828 5.77539L12.0303 6.17383C12.103 6.36986 12.2899 6.5 12.499 6.5Z"
                                fill="#412CC9"
                            />
                        </svg>
                    </button>
                </div>
                <el-dropdown trigger="click" class="header-action-item">
                    <span class="el-dropdown-link my-searches-btn">
                        My Searches
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9.75 4.5L6 8.25L2.25 4.5"
                                stroke="#667085"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    <template #dropdown>
                        <el-dropdown-menu>
                            <el-dropdown-item
                                @click.stop="openRecentSearchesModal"
                            >
                                <font-awesome-icon
                                    :icon="['far', 'clock']"
                                    class="me-2"
                                />
                                Recent Searches</el-dropdown-item
                            >
                            <el-dropdown-item @click.stop="goToSavedSearches"
                                ><font-awesome-icon
                                    :icon="['far', 'bookmark']"
                                    class="me-2"
                                />
                                Saved Searches</el-dropdown-item
                            >
                        </el-dropdown-menu>
                    </template>
                </el-dropdown>
            </div>

            <!-- Right Side - Notifications and User -->
            <div class="nav-right">
                <!-- Notifications -->
                <div class="nav-item notification-item mb-1">
                    <button
                        class="nav-btn notification-btn"
                        @click="openNotifyPopup()"
                    >
                        <img src="/img/notification-01.svg" />
                        <span
                            v-if="notifyBadge && unreadCount > 0"
                            class="notification-badge"
                        >
                            {{ unreadCount > 99 ? "+99" : unreadCount }}
                        </span>
                    </button>

                    <!-- Notifications Dropdown -->
                    <div class="notification-dropdown" v-if="notifyBadge">
                        <div class="notification-header">
                            <h6>Notifications</h6>
                        </div>
                        <div
                            v-if="loaderStore.isLoading"
                            class="notification-loading"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'spinner']"
                                spin
                            />
                        </div>
                        <div
                            v-else-if="!loaderStore.isLoading"
                            class="notification-list"
                        >
                            <div
                                v-for="(notification, index) in notifications"
                                :key="index"
                                class="notification-item"
                                :class="{
                                    unread:
                                        notification.resource_id != '' &&
                                        notification.is_seen == false,
                                }"
                                @click="markAsSeen(notification)"
                            >
                                <div class="notification-icon">
                                    <div class="icon-circle">
                                        <font-awesome-icon
                                            v-if="
                                                notification.status_type ===
                                                'approve'
                                            "
                                            :icon="['fas', 'check']"
                                            class="text-success"
                                        />
                                        <font-awesome-icon
                                            v-else-if="
                                                notification.status_type ===
                                                'reject'
                                            "
                                            :icon="['fas', 'xmark']"
                                            class="text-danger"
                                        />
                                        <font-awesome-icon
                                            v-else
                                            :icon="['fas', 'bell']"
                                            class="text-warning"
                                        />
                                    </div>
                                </div>
                                <div class="notification-content">
                                    <div class="notification-title">
                                        {{ notification.title }}
                                    </div>
                                    <div class="notification-time">
                                        {{ notification.created }}
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        notification.resource_id != '' &&
                                        notification.is_seen == false
                                    "
                                    class="notification-indicator"
                                ></div>
                            </div>
                            <div
                                v-if="notifications.length > 0"
                                class="view-all-notifications"
                            >
                                <router-link to="/notifications"
                                    >View all notifications</router-link
                                >
                            </div>
                        </div>
                        <div v-else class="no-notifications">
                            <h6>No New Notifications Yet</h6>
                        </div>
                    </div>
                </div>

                <!-- User Profile -->
                <div class="nav-item user-item">
                    <button
                        class="nav-btn user-btn"
                        @click="toggleUserDropdown"
                    >
                        <img
                            class="user-avatar"
                            :src="
                                useUserStore().getUserData.avatar ||
                                '../../../assets/img/default-avatar.jpg'
                            "
                            alt="User Avatar"
                        />
                    </button>

                    <!-- User Dropdown -->
                    <div v-if="showUserDropdown" class="user-dropdown">
                        <div class="user-info px-2">
                            <div class="user-avatar-large">
                                <img
                                    :src="
                                        useUserStore().getUserData.avatar ||
                                        '../../../assets/img/default-avatar.jpg'
                                    "
                                    alt="User Avatar"
                                />
                                <div class="online-indicator"></div>
                            </div>
                            <div class="user-details">
                                <div class="user-name">
                                    {{
                                        useUserStore().getUserData.name ||
                                        "User Name"
                                    }}
                                </div>
                                <div class="user-email">
                                    {{
                                        useUserStore().getUserData.email ||
                                        "user@example.com"
                                    }}
                                </div>
                            </div>
                        </div>
                        <hr/>
                        <div class="user-menu">
                            <div class="menu-item">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M240 192C240 147.8 275.8 112 320 112C364.2 112 400 147.8 400 192C400 236.2 364.2 272 320 272C275.8 272 240 236.2 240 192zM448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320C390.7 320 448 262.7 448 192zM144 544C144 473.3 201.3 416 272 416L368 416C438.7 416 496 473.3 496 544L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 544C544 446.8 465.2 368 368 368L272 368C174.8 368 96 446.8 96 544L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 544z"/></svg>
                                <span>View profile</span>
                            </div>
                            <div class="menu-item">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M259.1 73.5C262.1 58.7 275.2 48 290.4 48L350.2 48C365.4 48 378.5 58.7 381.5 73.5L396 143.5C410.1 149.5 423.3 157.2 435.3 166.3L503.1 143.8C517.5 139 533.3 145 540.9 158.2L570.8 210C578.4 223.2 575.7 239.8 564.3 249.9L511 297.3C511.9 304.7 512.3 312.3 512.3 320C512.3 327.7 511.8 335.3 511 342.7L564.4 390.2C575.8 400.3 578.4 417 570.9 430.1L541 481.9C533.4 495 517.6 501.1 503.2 496.3L435.4 473.8C423.3 482.9 410.1 490.5 396.1 496.6L381.7 566.5C378.6 581.4 365.5 592 350.4 592L290.6 592C275.4 592 262.3 581.3 259.3 566.5L244.9 496.6C230.8 490.6 217.7 482.9 205.6 473.8L137.5 496.3C123.1 501.1 107.3 495.1 99.7 481.9L69.8 430.1C62.2 416.9 64.9 400.3 76.3 390.2L129.7 342.7C128.8 335.3 128.4 327.7 128.4 320C128.4 312.3 128.9 304.7 129.7 297.3L76.3 249.8C64.9 239.7 62.3 223 69.8 209.9L99.7 158.1C107.3 144.9 123.1 138.9 137.5 143.7L205.3 166.2C217.4 157.1 230.6 149.5 244.6 143.4L259.1 73.5zM320.3 400C364.5 399.8 400.2 363.9 400 319.7C399.8 275.5 363.9 239.8 319.7 240C275.5 240.2 239.8 276.1 240 320.3C240.2 364.5 276.1 400.2 320.3 400z"/></svg>
                                <span>Saved Search</span>
                            </div>
                            <div class="menu-item logout-item" @click="logout">
                                <font-awesome-icon
                                    :icon="['fas', 'sign-out-alt']"
                                    class="me-2"
                                />
                                <span>Log out</span>
                                <span class="shortcut">~↑Q</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Search Modal Component -->
        <NewSearchComponent 
            :show="showSearchModal" 
            @close="closeSearchModal" 
            @view-all-results="openFullSearchResultsModal"
        />

        <!-- Recent Searches Sidebar Component -->
        <RecentSearchesSidebar
            :show="showRecentSearchesModal"
            @close="closeRecentSearchesModal"
            @search-selected="handleSearchSelected"
        />

        <!-- Full Search Results Sidebar Component -->
        <FullSearchResultsSidebar
            :show="showFullSearchResultsModal"
            :search-query="fullSearchQuery"
            :initial-tab="fullSearchInitialTab"
            @close="closeFullSearchResultsModal"
        />
    </nav>

    <!-- Global Modals moved from Sidebar -->
    <el-dialog
        v-model="createDocument"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :title="modalTitle"
        class="documents-modal"
        destroy-on-close
        :before-close="null"
    >
        <DocumentForm
            :default-category="defaultCategory"
            :default-project-id="defaultProjectId"
            :auto-proceed-to-step2="defaultAutoProceed"
            @update:title="modalTitle = $event"
            @create="afterCreateDocument"
        />
    </el-dialog>

    <el-dialog
        v-model="createProject"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :title="
            editProject
                ? $t('messages.edit_project')
                : $t('messages.add_project')
        "
        class="create-project-modal"
        destroy-on-close
    >
        <!-- :before-close="editProject ? onBeforeCloseProject : null" -->
        <ProjectForm
            :proj="editProject"
            @create="afterCreateProject"
            @update="createProject = false"
        />
    </el-dialog>

    <!-- List Modal -->
    <el-dialog
        v-model="createList"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :title="editList ? $t('messages.edit_list') : $t('messages.add_list')"
        class="create-list-modal"
        destroy-on-close
    >
        <!-- :before-close="editList ? onBeforeCloseList : null" -->
        <ListForm
            :list="editList"
            :item_ids="item_ids"
            :items_type="items_type"
            @create="afterCreateList"
            @update="handleListUpdated"
        />
    </el-dialog>

    <cancelModal
        v-model="showCancelDialog"
        :text="
            pendingCloseTarget === 'document'
                ? $t('messages.document_creation')
                : pendingCloseTarget === 'project'
                  ? editProject
                      ? $t('messages.project_editing')
                      : $t('messages.project_creation')
                  : pendingCloseTarget === 'list'
                    ? editList
                        ? 'List editing'
                        : 'List creation'
                    : $t('messages.project_creation')
        "
        @cancel="confirmCancel"
        @cancelButtonClose="backToDialog"
    />

    <AfterCreateProjectModal
        v-model="showAfterCreateProjectModal"
        :project-id="createdProjectId"
    />

    <AfterCreateListModal
        v-model="showAfterCreateListModal"
        :list-id="createdListId"
    />

    <AfterCreateDocumentModal
        v-model="showAfterCreateDocumentModal"
        :document-id="createdDocumentId"
    />
</template>

<style scoped>
.nav-center {
    max-width: 710px !important;
}
.header-action-item {
    width: 155px;
    margin-left: 15px;
    border: 1px solid #dfdce5;
    padding: 12px 9px;
    border-radius: 7px;
}
/* Search Container */
.search-container {
    position: relative;
    width: 100%;
}

.nav-link-icon {
    color: #96959D;
}

.search-input {
    cursor: pointer;
}

.navigation-text {
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
}

.navigate-arrow {
    border: 1px solid #dfdce5;
    border-radius: 5px;
}

.nav-btn.navigate-arrow:hover {
    background-color: #f0f0f0a9;
    box-shadow: 0 4px 12px #1018280D;
}

/* Searches Dropdown */
.searches-item {
    position: relative;
}

.searches-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 200px;
    z-index: 1000;
    margin-top: 8px;
}

.searches-header {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
}

.searches-header h6 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
}

.searches-menu {
    padding: 8px 0;
}

.searches-menu .menu-item {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    color: #374151;
    font-size: 14px;
}

.searches-menu .menu-item:hover {
    background-color: #f9fafb;
}

.menu-item svg {
    width: 20px;
}

.searches-menu .menu-item i {
    width: 16px;
    margin-right: 12px;
    color: #6b7280;
}
</style>
